import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, ShieldAlert } from "lucide-react";

import { Badge, Table, Td, Tr } from "../components/desk.tsx";
import { Button, Empty, ErrorBlock, Input, Spinner } from "../components/ui.tsx";
import { ReasonDialog } from "../components/reason-dialog.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { api } from "../api/client.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  后台 · 账号 / 角色
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **拆成两页。** 老板：「后台账号与角色做成分页的形式，一个账号管理
 *    一个角色管理，多使用列表，可以增删改查」。
 *
 *    原先是一页里塞两件事：上面一张账号表，下面四张只读的角色卡片。
 *    角色只看得见、改不了 —— 而"这个角色能干什么"恰恰是这套权限里
 *    最需要调的东西。卡片也没法横着比：四个角色差在哪，得一张张读过去。
 *
 * 🔴 **权限只是体验上的过滤，真正的闸门在服务端。** 这两页决定的是
 *    "谁能进后台、能看到哪几页"；接口那一层各自还有 `requireAdmin(...)`。
 *    改这里的时候别以为改的是安全边界。
 */

interface AdminRow {
  id: number;
  name: string;
  role: string;
  phone: string | null;
  disabled: boolean;
  lastLoginAt: string | null;
}

interface RoleRow {
  key: string;
  name: string;
  description: string;
  permissions: string[];
  builtin: boolean;
  enabled: boolean;
  memberCount: number;
}

interface PermGroup {
  group: string;
  items: { key: string; label: string; danger?: boolean }[];
}

function useRoles() {
  return useQuery({
    queryKey: ["admin", "roles"],
    queryFn: () => api.get<RoleRow[]>("/admin/roles"),
  });
}

// ═══════════════════════════ 账号 ═══════════════════════════

/**
 * 开账号 / 改账号，同一张表单。
 *
 * 🔴 **原来这里是连着弹三个 `window.prompt`** —— 手机号、姓名、
 *    然后让人**手打角色标识**（"superadmin / ops / finance"）。
 *    敲错一个字母就开出一个权限不对的账号，而这一页管的正是"谁能进后台"。
 *
 * ⚠️ 改的时候手机号是**只读**的：后台账号绑的是已存在的会员，
 *    换人等于换一个账号，应该删了重开而不是就地改绑 ——
 *    改绑之后历史操作记录会挂到另一个人头上。
 */
function AdminFormDialog({
  open,
  editing,
  roles,
  onClose,
}: {
  open: boolean;
  editing: AdminRow | null;
  roles: RoleRow[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ phone: "", name: "", role: "" });

  useEffect(() => {
    if (!open) return;
    setForm(
      editing
        ? { phone: editing.phone ?? "", name: editing.name, role: editing.role }
        : { phone: "", name: "", role: roles[0]?.key ?? "" },
    );
  }, [open, editing, roles]);

  const save = useMutation({
    mutationFn: () =>
      editing
        ? api.patch(`/admin/admins/${editing.id}`, { name: form.name.trim(), role: form.role })
        : api.post("/admin/admins", {
            phone: form.phone.trim(),
            name: form.name.trim(),
            role: form.role,
          }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin"] });
      onClose();
    },
  });

  const picked = roles.find((r) => r.key === form.role);
  const ready = form.name.trim() && form.role && (editing || form.phone.trim());

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{editing ? `编辑 · ${editing.name}` : "开一个后台账号"}</DialogTitle>
          <DialogDescription>
            后台账号绑的是<strong className="text-foreground">已经注册过的会员</strong>，
            不单独设密码 —— 走会员那套登录（含撞库防护、设备管理）。
            真正的隔离是：这张表里没有他，就进不了后台。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Input
            label="手机号"
            placeholder="要先注册过商城账号"
            inputMode="numeric"
            value={form.phone}
            /** ⚠️ 编辑时不让改绑：换人应该删了重开，否则历史操作会挂到另一个人头上。 */
            disabled={Boolean(editing)}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <Input
            label="姓名"
            placeholder="用于记录谁做了什么"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div>
            <span className="mb-1.5 block text-[13px] text-muted-foreground">角色</span>
            <select
              className="h-11 w-full rounded-lg border border-border bg-background px-3 text-[15px] outline-none focus:border-brand-500"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              {roles.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.name}
                </option>
              ))}
            </select>
            {/* ⚠️ 选完立刻把这个角色能干什么写出来 —— 光看名字看不出"财务"能不能审提现。 */}
            {picked && (
              <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">
                {picked.description}
                {picked.permissions[0] === "*"
                  ? "（全部权限）"
                  : `（${picked.permissions.length} 项权限）`}
              </p>
            )}
          </div>
        </div>

        {save.error && <ErrorBlock error={save.error} />}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button disabled={!ready} loading={save.isPending} onClick={() => save.mutate()}>
            {editing ? "保存" : "开账号"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdminAccountsTab() {
  const qc = useQueryClient();
  const [keyword, setKeyword] = useState("");
  const [formFor, setFormFor] = useState<AdminRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [askDelete, setAskDelete] = useState<AdminRow | null>(null);

  const admins = useQuery({
    queryKey: ["admin", "admins"],
    queryFn: () => api.get<AdminRow[]>("/admin/admins"),
  });
  const roles = useRoles();

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin"] });

  const patch = useMutation({
    mutationFn: (v: { id: number; disabled: boolean }) =>
      api.patch(`/admin/admins/${v.id}`, { disabled: v.disabled }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/admin/admins/${id}`),
    onSuccess: () => {
      setAskDelete(null);
      invalidate();
    },
  });

  const kw = keyword.trim();
  const rows = (admins.data ?? []).filter(
    (a) => !kw || a.name.includes(kw) || (a.phone ?? "").includes(kw),
  );
  const roleName = (key: string) => roles.data?.find((r) => r.key === key)?.name ?? key;

  return (
    <>

      <div className="mb-4 flex items-center gap-4">
        <div className="relative w-[280px]">
          <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-9 pl-10"
            placeholder="搜姓名或手机号"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>
        <Button
          size="sm"
          className="ml-auto"
          onClick={() => {
            setFormFor(null);
            setFormOpen(true);
          }}
        >
          <Plus className="size-3.5" />
          开一个账号
        </Button>
      </div>

      {admins.error ? (
        <ErrorBlock error={admins.error} />
      ) : admins.isPending ? (
        <Spinner compact />
      ) : rows.length === 0 ? (
        <Empty
          compact
          title={kw ? "没有符合条件的账号" : "还没有后台账号"}
          hint={
            kw
              ? "换个关键词试试，姓名和手机号都能搜。"
              : "后台账号绑的是已经注册过的会员 —— 先让他注册一个商城账号，再在这里开。"
          }
          action={kw ? { label: "清空搜索", onClick: () => setKeyword("") } : undefined}
        />
      ) : (
        <Table head={["姓名", "手机号", "角色", "最后登录", "状态", "操作"]}>
          {rows.map((a) => (
            <Tr key={a.id}>
              <Td className="font-medium">{a.name}</Td>
              <Td className="font-mono text-[13px] whitespace-nowrap select-all">
                {a.phone ?? "—"}
              </Td>
              <Td className="whitespace-nowrap">{roleName(a.role)}</Td>
              <Td className="whitespace-nowrap text-[12px] text-muted-foreground">
                {a.lastLoginAt ? new Date(a.lastLoginAt).toLocaleString("zh-CN") : "还没登录过"}
              </Td>
              <Td>
                <Badge tone={a.disabled ? "mute" : "ok"}>
                  {a.disabled ? "已停用" : "正常"}
                </Badge>
              </Td>
              <Td>
                <span className="flex items-center gap-3 whitespace-nowrap">
                  <button
                    className="text-[12px] text-brand-600 hover:underline"
                    onClick={() => {
                      setFormFor(a);
                      setFormOpen(true);
                    }}
                  >
                    编辑
                  </button>
                  {/*
                    ⚠️ **停用和删除是两件事，都要有。**
                       停用 = 暂时进不来（休假、待岗、正在查一件事），记录还在、随时能恢复；
                       删除 = 他不再是后台的人了（离职），这条授权撤掉。
                       只给停用的话，离职半年的人还挂在表里，谁也不敢删。
                  */}
                  <button
                    className="text-[12px] text-muted-foreground hover:underline"
                    onClick={() => patch.mutate({ id: a.id, disabled: !a.disabled })}
                  >
                    {a.disabled ? "启用" : "停用"}
                  </button>
                  <button
                    className="text-[12px] text-destructive hover:underline"
                    onClick={() => setAskDelete(a)}
                  >
                    删除
                  </button>
                </span>
              </Td>
            </Tr>
          ))}
        </Table>
      )}

      {patch.error && <ErrorBlock error={patch.error} />}
      {remove.error && <ErrorBlock error={remove.error} />}

      <AdminFormDialog
        open={formOpen}
        editing={formFor}
        roles={roles.data ?? []}
        onClose={() => setFormOpen(false)}
      />

      {/*
        ⚠️ 删除要说清"删的是什么"：撤的是后台权限，**不动他的商城会员账号** ——
           他可能还是这个平台的买家、账上还有钱。不说明白的话没人敢点。
      */}
      <ReasonDialog
        open={Boolean(askDelete)}
        title={`删除后台账号「${askDelete?.name ?? ""}」`}
        description="撤销他进后台的权限。⚠️ 不影响他的商城会员账号 —— 他仍然可以正常买东西、余额和订单都不动。只是暂时不让他进来的话，用「停用」。"
        placeholder="写清楚原因（离职 / 调岗 / 误开），记录在变更日志里"
        confirmLabel="确认删除"
        danger
        loading={remove.isPending}
        onConfirm={() => askDelete && remove.mutate(askDelete.id)}
        onClose={() => setAskDelete(null)}
      />
    </>
  );
}

// ═══════════════════════════ 角色 ═══════════════════════════

/**
 * 角色编辑器。
 *
 * 🔴 **高危权限要单独标出来。** 「审批退款（真的把钱退出去）」「导出证件号原文」
 *    跟「查看订单」摆在一起、一个样式，运营随手勾一片就把提现审批给出去了。
 */
function RoleFormDialog({
  open,
  editing,
  onClose,
}: {
  open: boolean;
  editing: RoleRow | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ key: "", name: "", description: "" });
  const [picked, setPicked] = useState<string[]>([]);

  const perms = useQuery({
    queryKey: ["admin", "permissions"],
    queryFn: () => api.get<PermGroup[]>("/admin/permissions"),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setForm(
      editing
        ? { key: editing.key, name: editing.name, description: editing.description }
        : { key: "", name: "", description: "" },
    );
    setPicked(editing ? editing.permissions.filter((p) => p !== "*") : []);
  }, [open, editing]);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/admin/roles/${form.key.trim()}`, {
        name: form.name.trim(),
        description: form.description.trim(),
        permissions: picked,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin"] });
      onClose();
    },
  });

  const toggle = (k: string) =>
    setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));

  const ready =
    form.name.trim() && /^[a-z][a-z0-9_-]{1,30}$/.test(form.key.trim()) && picked.length > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[720px]">
        <DialogHeader>
          <DialogTitle>{editing ? `编辑角色 · ${editing.name}` : "新建角色"}</DialogTitle>
          <DialogDescription>
            勾中的权限决定这个角色<strong className="text-foreground">能看到哪几页、能点哪些按钮</strong>。
            带 <ShieldAlert className="inline size-3.5 text-destructive" /> 的是高危权限 ——
            它们动的是钱和个人信息，给之前先想清楚。
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="角色名"
            placeholder="比如「仓管」"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Input
            label="标识"
            placeholder="小写字母 / 数字 / 下划线，如 warehouse"
            value={form.key}
            /** ⚠️ 标识是主键，建完不能改 —— 改了等于换一个角色，挂着它的人会读到空权限。 */
            disabled={Boolean(editing)}
            onChange={(e) => setForm({ ...form, key: e.target.value })}
          />
        </div>
        <Input
          label="说明"
          placeholder="一句话写清这个角色是干什么的，开账号时会显示给人看"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />

        <div className="mt-1 max-h-[42vh] space-y-4 overflow-y-auto rounded-lg border border-border p-4">
          {perms.isPending ? (
            <Spinner compact />
          ) : perms.error ? (
            <ErrorBlock error={perms.error} />
          ) : (
            (perms.data ?? []).map((g) => (
              <div key={g.group}>
                <p className="mb-2 text-[12px] font-medium tracking-wider text-muted-foreground">
                  {g.group}
                </p>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {g.items.map((it) => (
                    <label
                      key={it.key}
                      className={cn(
                        "flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-[13px] transition-colors hover:bg-secondary",
                        picked.includes(it.key) && "bg-brand-50",
                      )}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 size-4 shrink-0"
                        checked={picked.includes(it.key)}
                        onChange={() => toggle(it.key)}
                      />
                      <span className="min-w-0">
                        {it.danger && (
                          <ShieldAlert className="mr-1 inline size-3.5 text-destructive" />
                        )}
                        {it.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {save.error && <ErrorBlock error={save.error} />}
        <DialogFooter>
          <span className="mr-auto text-[12px] text-muted-foreground">
            已选 {picked.length} 项
          </span>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button disabled={!ready} loading={save.isPending} onClick={() => save.mutate()}>
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdminRolesTab() {
  const qc = useQueryClient();
  const [formFor, setFormFor] = useState<RoleRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [askDelete, setAskDelete] = useState<RoleRow | null>(null);

  const roles = useRoles();

  const remove = useMutation({
    mutationFn: (key: string) => api.del(`/admin/roles/${key}`),
    onSuccess: () => {
      setAskDelete(null);
      void qc.invalidateQueries({ queryKey: ["admin"] });
    },
  });

  return (
    <>

      <div className="mb-4 flex items-center">
        <Button
          size="sm"
          className="ml-auto"
          onClick={() => {
            setFormFor(null);
            setFormOpen(true);
          }}
        >
          <Plus className="size-3.5" />
          新建角色
        </Button>
      </div>

      {roles.error ? (
        <ErrorBlock error={roles.error} />
      ) : roles.isPending ? (
        <Spinner compact />
      ) : (roles.data ?? []).length === 0 ? (
        <Empty compact title="还没有角色" hint="内置角色会在第一次进后台时自动建出来。" />
      ) : (
        /*
          ⚠️ **用表格不用卡片。** 四个角色的差别是要**横着比**的：
             谁能审提现、谁能导证件号、谁只读。卡片得一张张读过去，
             而且卡片里塞不下"权限数 / 人数 / 内置与否 / 操作"这几列。
        */
        <Table head={["角色", "标识", "说明", "权限", "在用", "类型", "操作"]}>
          {(roles.data ?? []).map((r) => (
            <Tr key={r.key}>
              <Td className="font-medium whitespace-nowrap">{r.name}</Td>
              <Td className="font-mono text-[12px] text-muted-foreground">{r.key}</Td>
              <Td className="max-w-[380px] text-[13px] text-muted-foreground">{r.description}</Td>
              <Td className="whitespace-nowrap tabular-nums">
                {r.permissions[0] === "*" ? "全部" : `${r.permissions.length} 项`}
              </Td>
              <Td className="whitespace-nowrap tabular-nums">{r.memberCount} 人</Td>
              <Td>
                <Badge tone={r.builtin ? "mute" : "ok"}>
                  {r.builtin ? "内置" : "自定义"}
                </Badge>
              </Td>
              <Td>
                {r.builtin ? (
                  /*
                    ⚠️ 内置角色给一句"为什么不能改"，不要只是把按钮灰掉 ——
                       灰按钮不解释，人只会以为是权限不够或者页面坏了。
                  */
                  <span className="text-[12px] whitespace-nowrap text-muted-foreground">
                    内置，不可改
                  </span>
                ) : (
                  <span className="flex items-center gap-3 whitespace-nowrap">
                    <button
                      className="text-[12px] text-brand-600 hover:underline"
                      onClick={() => {
                        setFormFor(r);
                        setFormOpen(true);
                      }}
                    >
                      编辑
                    </button>
                    <button
                      className="text-[12px] text-destructive hover:underline"
                      onClick={() => setAskDelete(r)}
                    >
                      删除
                    </button>
                  </span>
                )}
              </Td>
            </Tr>
          ))}
        </Table>
      )}

      {remove.error && <ErrorBlock error={remove.error} />}

      <RoleFormDialog open={formOpen} editing={formFor} onClose={() => setFormOpen(false)} />

      {/*
        ⚠️ 还有人挂着的角色服务端会拒绝删除 —— 删掉之后那些人读到的是空权限，
           他们会突然发现后台什么都点不了，而没有任何提示告诉他们发生了什么。
           这里把人数写在弹窗里，让人在点之前就看到。
      */}
      <ReasonDialog
        open={Boolean(askDelete)}
        title={`删除角色「${askDelete?.name ?? ""}」`}
        description={
          askDelete && askDelete.memberCount > 0
            ? `⚠️ 现在还有 ${askDelete.memberCount} 个账号挂着这个角色，服务端会拒绝删除。请先把这些人改成别的角色。`
            : "删掉之后这个角色就不能再分配给任何人了。已经在用的账号不受影响（服务端会挡住还有人在用的角色）。"
        }
        placeholder="写清楚原因，记录在变更日志里"
        confirmLabel="确认删除"
        danger
        loading={remove.isPending}
        onConfirm={() => askDelete && remove.mutate(askDelete.key)}
        onClose={() => setAskDelete(null)}
      />
    </>
  );
}
